import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorCode } from '../errors/api-error-code';

/**
 * RFC 7807 ProblemDetail body returned by the API on error
 * (`Content-Type: application/problem+json`). Every error carries a `code` and a Czech `detail`
 * written for the user; the code is what the application branches on, the status is not.
 */
export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  /** Which error it is — one of the backend's `ErrorCode` constants, see {@link ApiErrorCode}. */
  code?: string;
  /** On `VALIDATION_FAILED`: the fields the API rejected. */
  fields?: { field: string; message: string }[];
}

export function problemCode(problem: ProblemDetail | null | undefined): string | undefined {
  const code = problem?.code;
  return typeof code === 'string' && code !== '' ? code : undefined;
}

/**
 * Reads the ProblemDetail body of a failed request. A `blob` responseType leaves the body an
 * undecoded Blob, so it is parsed back into JSON; anything unparseable yields `null`.
 */
export async function readProblemBody(error: HttpErrorResponse): Promise<ProblemDetail | null> {
  const body: unknown = error.error;
  if (body instanceof Blob) {
    try {
      return JSON.parse(await body.text()) as ProblemDetail;
    } catch {
      return null;
    }
  }
  return (body as ProblemDetail | null) ?? null;
}

/**
 * Whether an error is one of the three license refusals the license interceptor already explains
 * application-wide. Any other refusal — a missing membership, say — is nobody else's to explain, so
 * a call site with its own error rendering has to keep showing its own message for it.
 */
export function isLicenseRefusal(error: HttpErrorResponse): boolean {
  const code = problemCode(error.error as ProblemDetail | null);
  return (
    code === ApiErrorCode.SCHOOL_LICENSE_INACTIVE ||
    code === ApiErrorCode.LICENSE_TIER_INSUFFICIENT ||
    code === ApiErrorCode.AI_QUOTA_EXCEEDED
  );
}

export function isSvpLockConflict(error: HttpErrorResponse): boolean {
  return problemCode(error.error as ProblemDetail | null) === ApiErrorCode.SVP_LOCKED;
}

/**
 * Whether an error is an upload refused as larger than the request limit. The limit is enforced in
 * two places: the application answers with `FILE_TOO_LARGE`, but the container in front of it drops
 * an oversized request with a bare `413` before the application ever sees it, and that response
 * carries no ProblemDetail at all. The status is the only thing that identifies it, so this is the
 * one place a status is read — and only when there is no code to read instead.
 */
export function isFileTooLarge(error: HttpErrorResponse): boolean {
  const code = problemCode(error.error as ProblemDetail | null);
  return code === ApiErrorCode.FILE_TOO_LARGE || (code === undefined && error.status === 413);
}
